import {
    CopyOutlined,
    DollarOutlined,
    ExclamationCircleOutlined,
    FolderOpenOutlined,
} from "@ant-design/icons";
import { Alert, Button, Flex, Image, Tooltip } from "antd";
import { FC, ReactNode, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Market, UserRole } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { UniversalTabs } from "@/components/UniversalTabs";
import { ROUTES } from "@/constants";
import { useAppSelector } from "@/hooks/redux";
import {
    StyledAPContainer,
    StyledAPHeader,
    StyledAPImageWrapper,
    StyledBadge,
    StyledBadgeContainer,
} from "@/pages/WorkPlan/AboutPoint/Styled";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";
import { getFileUrl } from "@/utils/file/getFileUrl";

import { DebtTab } from "./DebtTab";
import { HistoryTab } from "./HistoryTab";
import { InfoTab } from "./InfoTab";
import { TasksTab } from "./TasksTab";

type TabValue = "info" | "history" | "debt" | "tasks";

interface TabConfig {
    label: string;
    value: TabValue;
    access?: UserRole[];
    requiresOnline?: boolean;
}

type BadgeBg = "error" | "warning";

type BadgeConfig = {
    key: string;
    visible: boolean;
    title: string;
    bg: BadgeBg;
    icon: ReactNode;
};

const allTabs: TabConfig[] = [
    {
        label: "Інфо",
        value: "info",
    },
    {
        label: "Історія",
        value: "history",
        access: [UserRole.Ta, UserRole.Sv],
        requiresOnline: true,
    },
    {
        label: "Дебіторка",
        value: "debt",
        requiresOnline: true,
        access: [UserRole.Ta, UserRole.Sv],
    },
    {
        label: "Задачі",
        value: "tasks",
        requiresOnline: true,
    },
];

interface AboutPointPageProps {
    market: Market;
}

export const AboutPointPage: FC<AboutPointPageProps> = ({ market }) => {
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const { role } = useAppSelector((state) => state.user);
    const { offlineMode } = useAppSelector((state) => state.offline);

    const marketId = market.id;

    const tabs = useMemo(
        () =>
            allTabs
                .filter((t) => {
                    if (t.access && !t.access.includes(role)) return false;
                    return true;
                })
                .map((t) => ({
                    ...t,
                    disabled: offlineMode && t.requiresOnline,
                })),
        [offlineMode, role],
    );

    const tab: TabValue = useMemo(() => {
        const fromQuery = (
            searchParams.get("tab") || ""
        ).toLowerCase() as TabValue;

        const tabExists = tabs.some((t) => t.value === fromQuery);

        if (tabExists) {
            return fromQuery;
        }

        return "info";
    }, [searchParams, tabs]);

    const setTab = (next: TabValue) => {
        const targetTab = tabs.find((t) => t.value === next);

        if (targetTab && !targetTab.disabled) {
            setSearchParams(
                (prev) => {
                    const sp = new URLSearchParams(prev);
                    sp.set("tab", next);
                    return sp;
                },
                { replace: true },
            );
        }
    };

    const tabComponent = useMemo(
        () => ({
            info: <InfoTab market={market} />,
            history: <HistoryTab marketId={marketId} />,
            debt: <DebtTab hasDebt={market?.hasDebt} />,
            tasks: <TasksTab market={market} />,
        }),
        [market, marketId],
    );

    const imageURL = getFileUrl(market?.photo);

    const addressAlertVisible =
        !market?.address?.id &&
        !offlineMode &&
        (role === UserRole.Sv || role === UserRole.Svm);

    const goToVerify = () => {
        navigate(buildRoutePath(ROUTES.MarketVerifyAddress, { marketId }));
    };

    const handleCopyAddress = useCallback(() => {
        copyObjectToClipboard(market.address);
    }, [market.address]);

    const badges = useMemo<BadgeConfig[]>(
        () => [
            {
                key: "debt",
                visible: market.hasDebt || market.hasDebtCritical,
                title: "Торгова точка має заборгованість",
                bg: "error",
                icon: <DollarOutlined />,
            },
            {
                key: "archived",
                visible: market.isArchived,
                title: "Торгова точка є архівною",
                bg: "warning",
                icon: <FolderOpenOutlined />,
            },
        ],
        [market.hasDebt, market.hasDebtCritical, market.isArchived],
    );

    const visibleBadges = badges.filter((b) => b.visible);

    return (
        <StyledAPContainer>
            <StyledAPHeader>
                <StyledAPImageWrapper>
                    {visibleBadges.length > 0 && (
                        <StyledBadgeContainer>
                            {visibleBadges.map((b) => (
                                <Tooltip key={b.key} title={b.title}>
                                    <StyledBadge $background={b.bg}>
                                        {b.icon}
                                    </StyledBadge>
                                </Tooltip>
                            ))}
                        </StyledBadgeContainer>
                    )}

                    <Image src={imageURL} alt={marketId} />
                </StyledAPImageWrapper>

                <Flex vertical gap={4}>
                    <PrimaryText fontSize={16}>
                        {market.contragent.name}
                    </PrimaryText>

                    <Flex gap={4} align="center">
                        <SecondaryText>{market.address.name}</SecondaryText>

                        <Button
                            style={{ flexShrink: 0 }}
                            icon={<CopyOutlined />}
                            type="text"
                            onClick={handleCopyAddress}
                        />
                    </Flex>
                </Flex>
            </StyledAPHeader>

            {addressAlertVisible && (
                <Alert
                    type="warning"
                    showIcon
                    icon={
                        <ExclamationCircleOutlined style={{ fontSize: 18 }} />
                    }
                    message={
                        <Flex align="center" gap={8} wrap="wrap">
                            <PrimaryText fontSize={14}>
                                Адресу не верифіковано.
                            </PrimaryText>
                        </Flex>
                    }
                    action={
                        <Button type="primary" onClick={goToVerify}>
                            <PrimaryText $textColor="white">
                                Верифікувати
                            </PrimaryText>
                        </Button>
                    }
                    style={{ marginTop: 12 }}
                />
            )}

            <UniversalTabs tabs={tabs} onChange={setTab} value={tab} />

            {tabComponent[tab]}
        </StyledAPContainer>
    );
};
